export type Superuser = {
  id: string;
  email: string;
  displayName: string;
};

export type AuthSession = {
  user: Superuser;
  csrfToken: string;
};

export type MeetingRecord = {
  id: string;
  title: string;
  state: string;
  createdAt: number;
  startedAt?: number | null;
  finalizedAt?: number | null;
  playbackReady?: boolean;
};

export type FinalizeResult = {
  meeting: MeetingRecord;
  missingSequences: number[];
  playbackReady: boolean;
};

export type MeetingUploadStatus = {
  meeting: MeetingRecord;
  expectedLastSequence: number | null;
  chunks: Array<{
    sequence: number;
    byteLength: number;
    contentType: string;
    sha256: string;
  }>;
};

export type ArchiveLocale = "en" | "id" | "zh" | "th";

export type MeetingTranscriptSegment = {
  id: string;
  sourceSpeakerLabel: string;
  speakerLabel: string;
  startMs: number;
  endMs: number;
  text: string;
};

export type MeetingSummaryContent = {
  overview: string;
  keyPoints: string[];
  decisions: string[];
  actionItems: string[];
  openQuestions: string[];
};

export type MeetingProcessingState = {
  state: string;
  lastError?: string | null;
  canRetry?: boolean;
};

export type MeetingArchive = {
  meeting: MeetingRecord;
  processing: MeetingProcessingState;
  transcript: null | {
    id: string;
    model: string;
    segments: MeetingTranscriptSegment[];
  };
  summaries: Array<{
    locale: ArchiveLocale;
    content: MeetingSummaryContent;
  }>;
};

export class MeetingApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export async function login(email: string, password: string): Promise<AuthSession> {
  return apiJson<AuthSession>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
    headers: { "Content-Type": "application/json" },
  });
}

export async function currentSession(): Promise<AuthSession | null> {
  const response = await fetch("/api/auth/me", {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  if (response.status === 401) return null;
  const result = await readJson<{ user: Superuser }>(response);
  const csrfToken = readCookie("__Host-table_interpreter_csrf")
    ?? readCookie("table_interpreter_csrf");
  return csrfToken ? { user: result.user, csrfToken } : null;
}

export async function logout(csrfToken: string): Promise<void> {
  await apiJson("/api/auth/logout", mutationInit(csrfToken, {}));
}

export async function createMeeting(
  csrfToken: string,
  input: { id: string; title: string; consentConfirmed: true },
): Promise<MeetingRecord> {
  return apiJson<MeetingRecord>("/api/meetings", mutationInit(csrfToken, input));
}

export async function uploadRecordingChunk(options: {
  csrfToken: string;
  meetingId: string;
  sequence: number;
  blob: Blob;
  sha256: string;
  durationMs?: number;
}): Promise<{ acknowledgedSequence: number }> {
  const response = await fetch(
    `/api/meetings/${encodeURIComponent(options.meetingId)}/chunks/${options.sequence}`,
    {
      method: "POST",
      body: options.blob,
      credentials: "same-origin",
      headers: {
        "Content-Type": options.blob.type || "application/octet-stream",
        "X-Chunk-SHA256": options.sha256,
        "X-CSRF-Token": options.csrfToken,
        ...(options.durationMs === undefined
          ? {}
          : { "X-Chunk-Duration-Ms": String(options.durationMs) }),
      },
    },
  );
  return readJson<{ acknowledgedSequence: number }>(response);
}

export async function finalizeMeeting(
  csrfToken: string,
  meetingId: string,
  lastSequence: number,
): Promise<FinalizeResult> {
  return apiJson<FinalizeResult>(
    `/api/meetings/${encodeURIComponent(meetingId)}/finalize`,
    mutationInit(csrfToken, { lastSequence }),
  );
}

export async function getMeeting(meetingId: string): Promise<MeetingRecord> {
  return apiJson<MeetingRecord>(`/api/meetings/${encodeURIComponent(meetingId)}`, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
}

export async function listMeetings(): Promise<MeetingRecord[]> {
  const result = await apiJson<{ meetings: MeetingRecord[] }>("/api/meetings", {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  return result.meetings;
}

export async function getMeetingArchive(meetingId: string): Promise<MeetingArchive> {
  return apiJson<MeetingArchive>(
    `/api/meetings/${encodeURIComponent(meetingId)}/archive`,
    {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    },
  );
}

export async function retryMeetingProcessing(
  csrfToken: string,
  meetingId: string,
): Promise<MeetingProcessingState> {
  const result = await apiJson<MeetingProcessingState | { processing: MeetingProcessingState }>(
    `/api/meetings/${encodeURIComponent(meetingId)}/retry-processing`,
    mutationInit(csrfToken, {}),
  );
  return "processing" in result ? result.processing : result;
}

export async function renameMeetingSpeaker(
  csrfToken: string,
  meetingId: string,
  sourceSpeakerLabel: string,
  displayName: string,
): Promise<void> {
  await apiJson(
    `/api/meetings/${encodeURIComponent(meetingId)}/speakers/${encodeURIComponent(sourceSpeakerLabel)}`,
    mutationInit(csrfToken, { displayName }, "PATCH"),
  );
}

export async function getMeetingUploadStatus(meetingId: string): Promise<MeetingUploadStatus> {
  return apiJson<MeetingUploadStatus>(
    `/api/meetings/${encodeURIComponent(meetingId)}/upload-status`,
    {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    },
  );
}

export function meetingAudioUrl(meetingId: string): string {
  return `/api/meetings/${encodeURIComponent(meetingId)}/audio`;
}

export function meetingAudioDownloadUrl(meetingId: string): string {
  return `/api/meetings/${encodeURIComponent(meetingId)}/downloads/audio`;
}

export function meetingTranscriptDownloadUrl(meetingId: string): string {
  return `/api/meetings/${encodeURIComponent(meetingId)}/downloads/transcript`;
}

export function meetingSummaryDownloadUrl(meetingId: string, locale: ArchiveLocale): string {
  return `/api/meetings/${encodeURIComponent(meetingId)}/downloads/summary/${locale}`;
}

function mutationInit(
  csrfToken: string,
  body: unknown,
  method: "POST" | "PATCH" = "POST",
): RequestInit {
  return {
    method,
    body: JSON.stringify(body),
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
    },
  };
}

async function apiJson<T = unknown>(url: string, init: RequestInit): Promise<T> {
  const response = await fetch(url, { credentials: "same-origin", ...init });
  return readJson<T>(response);
}

async function readJson<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => ({})) as {
    error?: string;
    code?: string;
  } & T;
  if (!response.ok) {
    throw new MeetingApiError(
      response.status,
      body.code ?? "request-failed",
      body.error ?? "Request failed",
    );
  }
  return body;
}

function readCookie(name: string): string | undefined {
  for (const part of document.cookie.split(";")) {
    const [cookieName, ...valueParts] = part.trim().split("=");
    if (cookieName === name) return valueParts.join("=") || undefined;
  }
  return undefined;
}
